import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex, Legend, Spacer } from '@zillow/constellation';
import {
  AdornedInput,
  Box,
  FieldSet,
  FormField,
  FormHelp,
  Label,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import {
  FeeType,
  stringHelper,
  useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';
import { setQueryApplicationsSummary } from '../../../../hooks/queries/summary';

import PostingActions from '../../../../actions/PostingActions';
import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';

import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';
import { TRIGGER_LOCATIONS } from '../../../../constants/ClickstreamConstants';

import { costsAndFeesConstraints } from '../../../../validation';

import WizardServerError from '../../../../model/WizardServerError';
import ModularListingCSEvents from '../../../../model/ClickstreamEvent/ModularListingCSEvents';
import ReviewPageCSEvents from '../../../../model/ClickstreamEvent/ReviewPageCSEvents';

import WizardAlert from '../../../../components/WizardAlert';
import { ApplicationsLabeledControl } from '../../../../components/ApplicationsLabeledControl';
import ApplicationsIntroModal from '../../../Modal/ApplicationsIntroModal';

import type { MutableRefObject } from 'react';

import type {
  CostAndFeeStyledComponents,
  FeeCalculationType,
  FeeRefundableType,
  FeeRequirementType,
  FeeTimingSubType,
  FeeTimingType,
  FeeTypeGrouping,
  SingleFamilyFeeDto,
  WebAnalyticsWebResponseDto,
} from '@zg-rentals/rental-manager-env';

const fieldNames = {
  applicationFeeAmount: 'applicationFeeAmount',
  isApplicationsAccepted: 'isApplicationsAccepted',
};

const radioControlName = fieldNames.isApplicationsAccepted;

const csEventMapperML: Record<
  string,
  | typeof ModularListingCSEvents.ApplicationsEnableFromDisabled
  | typeof ModularListingCSEvents.ApplicationsDisableFromEnabled
> = {
  EnableFromDisabled: ModularListingCSEvents.ApplicationsEnableFromDisabled,
  DisableFromEnabled: ModularListingCSEvents.ApplicationsDisableFromEnabled,
};

const csEventMapperReviewPage: Record<
  string,
  | typeof ReviewPageCSEvents.ApplicationsEnableFromNull
  | typeof ReviewPageCSEvents.ApplicationsDisableFromNull
  | typeof ReviewPageCSEvents.ApplicationsEnableFromDisabled
  | typeof ReviewPageCSEvents.ApplicationsDisableFromEnabled
> = {
  EnableFromNull: ReviewPageCSEvents.ApplicationsEnableFromNull,
  EnableFromDisabled: ReviewPageCSEvents.ApplicationsEnableFromDisabled,
  DisableFromEnabled: ReviewPageCSEvents.ApplicationsDisableFromEnabled,
  DisableFromNull: ReviewPageCSEvents.ApplicationsDisableFromNull,
};

type Props = {
  isRelistingFieldset?: boolean;
  triggerLocation?: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
};

const getInitialDollarAmount = (value: number, ref: MutableRefObject<boolean>) => {
  return ref.current ? (Number.isInteger(Number(value)) ? Number(value) : Number(value).toFixed(2)) : value;
};

const AcceptZillowApplicationsFieldSet = ({
  triggerLocation = TRIGGER_LOCATIONS.POSTING_PATH,
  isRelistingFieldset = false,
}: Props) => {
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, []);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { canAccessSFCostsAndFees, isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty: { isApplicationsAccepted, isMultifamily, listingAlias },
    },
    user,
  } = useSelector((state) => state);

  const [displayIntroModal, setDisplayIntroModal] = useState(false);

  const { refetch: refetchWebAnalytics } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: Boolean(listingAlias) },
  );

  const { data: applicationFee = [] } = useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType(
    {
      feeType: FeeType.APPLICATION_FEE,
      listingAlias,
    },
    {
      enabled: canAccessSFCostsAndFees && listingAlias !== '',
    },
  );

  const {
    errorObj,
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      isApplicationsAccepted: isApplicationsAccepted === null ? '' : isApplicationsAccepted.toString(),
      applicationFeeAmount: applicationFee?.length
        ? getInitialDollarAmount(applicationFee?.[0]?.feeAmountMin, isInitialRender)
        : null,
    },
    // @ts-expect-error
    { applicationFeeAmount: costsAndFeesConstraints?.values?.feeAmountMinFlat },
  );

  // TODO: might need to put this into context for consistent state sharing
  const isReviewStep = useMemo(() => {
    return stringHelper.getLastPieceFromPath(location.pathname) === 'listing';
  }, [location.pathname]);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: radioControlName,
          value: inputs.isApplicationsAccepted,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const handleClickstreamEvent = ({
    data,
    clickstreamEventName,
  }: {
    data?: WebAnalyticsWebResponseDto;
    clickstreamEventName: string;
  }): void => {
    if (isReviewStep === false) {
      csEventMapperML[clickstreamEventName]?.({
        user,
        currentProperty: data ?? {},
        triggerLocation,
      });
    } else {
      csEventMapperReviewPage[clickstreamEventName]?.({
        user,
        currentProperty: data ?? {},
      });
    }
  };

  // @ts-expect-error
  const save = (event) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    if (hasFieldError(fieldName, value)) {
      return;
    }

    if (
      inputs.isApplicationsAccepted === 'false' &&
      hasFieldError(fieldNames.applicationFeeAmount, inputs.applicationFeeAmount)
    ) {
      // @ts-expect-error
      updateServerError(errorObj.applicationFeeAmount, fieldNames.applicationFeeAmount);
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: true });
      return;
    }

    const dataToSave: {
      fieldName: string;
      value: string;
      propertyId: string;
      additionalParams: {
        inviteOnly: false;
        applicationFeeAmount?: number;
      };
    } = {
      fieldName,
      value,
      propertyId: listingAlias,
      additionalParams: {
        inviteOnly: false,
      },
    };

    if (canAccessSFCostsAndFees && inputs.isApplicationsAccepted === 'false') {
      if (shouldSaveOnBlur && fieldName === fieldNames.applicationFeeAmount) {
        dataToSave.fieldName = fieldNames.isApplicationsAccepted;
        dataToSave.value = inputs.isApplicationsAccepted;
        dataToSave.additionalParams.applicationFeeAmount = value;
      } else if (inputs.applicationFeeAmount !== null) {
        dataToSave.additionalParams.applicationFeeAmount = inputs.applicationFeeAmount as number;
      }
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      // @ts-expect-error
      .then(() => {
        const updatedValue = value === 'true';
        const toSelectedDisplay = updatedValue ? 'Enable' : 'Disable';

        let fromSelectedDisplay = 'Null';
        if (isApplicationsAccepted !== null) {
          fromSelectedDisplay = isApplicationsAccepted ? 'Enabled' : 'Disabled';
        }

        refetchWebAnalytics()
          .then((response) => {
            handleClickstreamEvent({
              data: response.data,
              clickstreamEventName: `${toSelectedDisplay}From${fromSelectedDisplay}`,
            });
          })
          .catch(() =>
            logError({
              error: new Error('Web Analytics API call failed'),
              errorType: 'RefetchWebAnalyticsError',
              context: 'AcceptZillowApplicationsFieldSet',
            }),
          );

        dispatch(
          PropertiesActions.updateCurrentProperty({
            isApplicationsAccepted: updatedValue,
          }),
        );

        syncWithQueryCache({ isApplicationsAccepted: updatedValue });

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

        const getApplicationFeeQueryKey =
          useRentalManagerUserPropertySingleFamilyCostAndFeesWebApiHandler_getFeesForType.getKey({
            feeType: FeeType.APPLICATION_FEE,
            listingAlias,
          });

        // set the query data to include the updated fee amount so we don't see a flash of the old fee amount on the UI
        // BE doesn't return all the fee data so we have to set the query data manually
        // but the feeAmountMin is the only one we actually display/use for this fee
        queryClient.setQueryData<Array<SingleFamilyFeeDto>>(getApplicationFeeQueryKey, () => {
          const existingFeeData = applicationFee?.length > 0 ? applicationFee[0] : null;
          const newFeeData: SingleFamilyFeeDto = {
            // @ts-expect-error
            feeAmountMin: inputs.applicationFeeAmount,
            // @ts-expect-error
            feeAmountMax: inputs.applicationFeeAmount,
            feeType: existingFeeData?.feeType || FeeType.APPLICATION_FEE,
            listingAlias,
            costAndFeeId: existingFeeData?.costAndFeeId || '',
            feeName: existingFeeData?.feeName || 'Application Fee',
            feeCategory: existingFeeData?.feeCategory || ('APPLICATION' as FeeTypeGrouping),
            feeCreatedMs: existingFeeData?.feeCreatedMs || Date.now(),
            feePaymentFrequencyCategoryType:
              existingFeeData?.feePaymentFrequencyCategoryType || ('ONE_TIME' as FeeTimingType),
            feePaymentFrequencyType: existingFeeData?.feePaymentFrequencyType || ('MOVE_IN' as FeeTimingSubType),
            feeRequirementType: existingFeeData?.feeRequirementType || ('MANDATORY' as FeeRequirementType),
            styledComponents: existingFeeData?.styledComponents || ({} as CostAndFeeStyledComponents),
            feeCalculationType: existingFeeData?.feeCalculationType || ('FLAT_FEE' as FeeCalculationType),
            feeDescription: existingFeeData?.feeDescription || '',
            feeRefundableType: existingFeeData?.feeRefundableType || ('NON_REFUNDABLE' as FeeRefundableType),
          };
          return [newFeeData];
        });
        // invalidate query to refresh the actual fee data in the background
        queryClient.invalidateQueries({ queryKey: getApplicationFeeQueryKey });

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      // @ts-expect-error
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const syncWithQueryCache = ({ isApplicationsAccepted }: { isApplicationsAccepted: boolean }) => {
    setQueryApplicationsSummary({
      config: {
        queryClient,
        listingAlias,
        getActive: true,
      },
      payload: {
        isApplicationsAccepted,
      },
    });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), [shouldSaveOnBlur]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();

    clearServerError();

    if (event.target.name === fieldNames.applicationFeeAmount && event.target.value !== '') {
      event.target.valueAsNumber = Number(Number(event.target.valueAsNumber).toFixed(2));
    }

    // @ts-expect-error
    handleFormChange(event);

    if (
      event.target.name === fieldNames.applicationFeeAmount &&
      hasFieldError(fieldNames.applicationFeeAmount, event.target.value)
    ) {
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: true });
      return;
    }

    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });
    saveDebounced(event);
  };

  const renderLegendContent = () => {
    return (
      <Flex display="flex" flexDirection={{ default: 'column', md: 'row' }}>
        <Legend fontType="bodyHeading" paddingBottom="xxs">
          Receive Zillow applications for this listing?
        </Legend>
        <Flex display="flex" alignItems="center" paddingLeft={{ default: '0', md: 'sm' }}>
          <Tag tone="info" size="sm">
            FREE
          </Tag>
          <TextButton
            textStyle="body-bold"
            css={{ paddingLeft: '400' }}
            onClick={() => {
              setDisplayIntroModal(true);
            }}
          >
            Learn more
          </TextButton>
        </Flex>
      </Flex>
    );
  };

  return (
    <div data-test="ApplicationsFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet
        className="AcceptZillowApplicationsFieldSet"
        // keep required set for screen readers
        required={true}
        legend={isRelistingFieldset ? renderLegendContent() : null}
      >
        {isRelistingFieldset && (
          <Spacer marginTop="xs" marginBottom="xxs">
            <Text css={{ color: 'text.neutral' }}>
              Your listing gets an additional promotion when you choose to receive Zillow applications.
            </Text>
          </Spacer>
        )}
        <Flex
          display="flex"
          paddingLeft={{ default: 'xs', lg: isRelistingFieldset ? '0' : 'xs' }}
          flexDirection={{ default: 'column', lg: isRelistingFieldset ? 'row' : 'column' }}
        >
          {/* @ts-expect-error */}
          <ApplicationsLabeledControl isApplicationsAccepted={inputs.isApplicationsAccepted} onChange={handleChange} />
        </Flex>
      </FieldSet>
      {canAccessSFCostsAndFees && !isMultifamily && inputs.isApplicationsAccepted === 'false' && (
        <FieldSet>
          <Box css={{ paddingLeft: { base: '200', lg: isRelistingFieldset ? '0' : '800' } }}>
            <FormField
              label={<Label required={true}>Fee amount</Label>}
              control={
                <AdornedInput
                  css={{ maxWidth: '360px' }}
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                  input={
                    <AdornedInput.Input
                      name="applicationFeeAmount"
                      value={String(inputs.applicationFeeAmount || '')}
                      onChange={handleChange}
                      type="number"
                    />
                  }
                  endAdornment={<AdornedInput.Adornment>/applicant</AdornedInput.Adornment>}
                />
              }
              description={
                <Box css={{ paddingTop: '100', minHeight: '600' }}>
                  <FormHelp
                    // @ts-expect-error
                    error={errorObj.applicationFeeAmount && errorObj.applicationFeeAmount !== null}
                  >
                    {/* @ts-expect-error */}
                    {errorObj.applicationFeeAmount ||
                      `Include the cost of the background check and credit report. Enter $0 if you won't charge a fee.`}
                  </FormHelp>
                </Box>
              }
            />
          </Box>
        </FieldSet>
      )}

      {isRelistingFieldset && displayIntroModal && (
        <ApplicationsIntroModal onClose={() => setDisplayIntroModal(false)} />
      )}
    </div>
  );
};

export default AcceptZillowApplicationsFieldSet;
